import React, { useMemo } from 'react';
import RentleDataGrid from 'components/datagrid/RentleDataGrid';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import { customerToCustomersTableRow } from './utils/rows';
import { getCustomersTableColumns } from './utils/columns';
import CustomersTableToolbar from './CustomersTableToolbar';
import CustomNoRowsOverlay from '../datagrid/CustomNoRowsOverlay';
import { useTranslation } from 'services/localization';
import { CustomerDbModel } from 'common/modules/customers';

export interface Props {
	customers: CustomerDbModel[];
	limit: number;
	searchValue?: string;
	dateRange: string;
	loading: boolean;
	pageSize: number;
	onPageSizeChange: (pageSize: number) => void;
	page: number;
	onPageChange: (page: number) => void;
}
const BaseCustomersTable = (props: Props) => {
	const { t } = useTranslation();
	const apiRef = useGridApiRef();

	const {
		customers,
		searchValue,
		dateRange,
		loading,
		pageSize,
		onPageSizeChange,
		page,
		onPageChange,
	} = props;

	const categoriesById = useSelector(StockSelectors.categoriesById);
	const productsById = useSelector(StockSelectors.stockProductsById);
	const dateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const currency = useSelector(ShopSelectors.getShopCurrency);
	const defaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);

	const rows = useMemo(() => {
		const mapFunction = customerToCustomersTableRow({
			defaultLanguage,
			categoriesById,
			productsById,
			t,
		});
		return customers.map(mapFunction);
	}, [defaultLanguage, categoriesById, productsById, customers, t]);

	const columns = useMemo(() => {
		return getCustomersTableColumns({
			dateFormat,
			currency,
			t,
		});
	}, [currency, dateFormat, t]);

	return (
		<RentleDataGrid
			apiRef={apiRef}
			rows={rows}
			sx={{
				'.MuiDataGrid-row': {
					cursor: 'default',
				},
			}}
			columns={columns}
			density="standard"
			autoHeight
			components={{
				Toolbar: CustomersTableToolbar,
				NoResultsOverlay: CustomNoRowsOverlay,
			}}
			pageSize={pageSize}
			page={page}
			onPageChange={onPageChange}
			onPageSizeChange={onPageSizeChange}
			pagination
			loading={loading}
			componentsProps={{
				noRowsOverlay: {
					title: !!searchValue
						? t('customers.noMatchingCustomers', {
								searchValue: searchValue,
								defaultValue: 'No matching customers found for {{searchValue}}',
						  })
						: t('customers.noCustomers', 'You have no customers yet'),
					body: !!searchValue
						? ''
						: t(
								'customers.noCustomersDescription',
								'Once you get your first customer they will be visible here',
						  ),
				},
				toolbar: {
					dateRange,
				},
			}}
		/>
	);
};

export default BaseCustomersTable;
